import { ChangeDetectionStrategy, Component, input, model, output } from '@angular/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';

/**
 * WidgetCard Component
 *
 * A flexible card container for dashboard widgets with optional header actions.
 * Used for charts, lists, and other dashboard content.
 *
 * @example
 * ```html
 * <zag-widget-card title="Today's Shifts" actionLabel="View All" (actionClick)="viewAll()">
 *   <!-- content -->
 * </zag-widget-card>
 * ```
 */
@Component({
  selector: 'zag-widget-card',
  imports: [ZagButton, ZagIcon, ScrollIntoViewDirective],
  template: `
    <div
      class="zag-widget-card__header"
      [class.zag-widget-card__header--collapsed]="collapsible() && !expanded()"
      [zagScrollIntoView]="collapsible()"
      [scrollOnSignal]="expanded"
      [scrollEvents]="[]"
      scrollBlock="start"
    >
      @if (collapsible()) {
        <div
          class="zag-widget-card__toggle"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="expanded()"
          (click)="toggle()"
          (keydown.enter)="toggle()"
          (keydown.space)="$event.preventDefault(); toggle()"
        >
          <h2 class="zag-widget-card__title">{{ title() }}</h2>
          <span zagIcon="expand_more" class="zag-widget-card__chevron" [class.zag-widget-card__chevron--expanded]="expanded()"></span>
        </div>
      } @else {
        <h2 class="zag-widget-card__title">{{ title() }}</h2>
      }
      @if (actionLabel()) {
        <button zagButton zagType="primary" class="zag-widget-card__action" (click)="actionClick.emit()">
          {{ actionLabel() }}
        </button>
      }
      <ng-content select="[slot=action]"></ng-content>
    </div>
    @if (!collapsible() || expanded()) {
      <div class="zag-widget-card__content">
        <ng-content></ng-content>
      </div>
    }
    @if (showFooter()) {
      <div class="zag-widget-card__footer">
        <ng-content select="[slot=footer]"></ng-content>
      </div>
    }
  `,
  styleUrl: './widget-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-widget-card]': 'true',
  },
})
export class WidgetCard {
  /** Card title */
  readonly title = input.required<string>();

  /** Optional action button label */
  readonly actionLabel = input<string>('');

  /** Whether to show footer slot */
  readonly showFooter = input<boolean>(false);

  /** Whether the title row toggles the content */
  readonly collapsible = input<boolean>(false);

  /** Content visibility; only applies when collapsible */
  readonly expanded = model<boolean>(true);

  /** Emits when action button is clicked */
  readonly actionClick = output<void>();

  protected toggle(): void {
    this.expanded.update(open => !open);
  }
}
